"use client";

import * as React from "react";
import { CircleAlert, BadgeDollarSign, Clock, FileX } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const problemCards = [{
  title: "Agency Finder Fees",
  description: "Recruitment agencies charge 15–25% of annual salary. For a senior PM, that’s NZD 20,000–$40,000 before a single day of work begins.",
  icon: BadgeDollarSign
}, {
  title: "Idle Billing",
  description: "Contract PMs charge by the day — whether they are productive or not. You pay for public holidays, slow periods, and waiting time.",
  icon: Clock
}, {
  title: "No Guaranteed Output",
  description: "Traditional contracts define time, not deliverables. There is no obligation to produce specific outcomes — just show up.",
  icon: FileX
}];
const costStats = [{
  value: "20–25%",
  description: "of annual PM salary paid as agency finder fee — before day one"
}, {
  value: "30–40%",
  description: "of contract PM time is non-billable admin, waiting, or idle periods"
}];
export default function ProblemSolutionCostComparison() {
  const {
    ref: problemHeaderRef,
    isVisible: isProblemHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: cardsRef,
    isVisible: areCardsVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: alertRef,
    isVisible: isAlertVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: costHeaderRef,
    isVisible: isCostHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: statsRef,
    isVisible: areStatsVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="problemsolutioncostcomparison" className="bg-[#0d1b2a] bg-linear-to-b from-[#060f1a] to-[#0d1b2a] text-foreground overflow-hidden py-10 md:py-14">
      <div className="mx-auto max-w-7xl px-4 md:px-8">
        <div className="flex flex-col items-center">
          <div ref={problemHeaderRef} className={`flex max-w-3xl flex-col items-center text-center transition-all duration-1000 ${isProblemHeaderVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
            <span className="text-[#00c4b4] mb-4 text-xs font-bold tracking-widest uppercase">
              The Problem | Overheads in Traditional Project Management
            </span>
            <h2 className="mb-6 text-4xl font-extrabold tracking-tight text-white md:text-5xl lg:text-6xl">
              The Traditional PM Model Is Broken.
            </h2>
            <h3 className="text-white/70 text-lg md:text-xl">
              Organisations pay too much, wait too long, and get too little
              accountability.
            </h3>
          </div>

          <div ref={cardsRef} className={`mt-6 grid w-full grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 transition-all duration-1000 delay-200 ${areCardsVisible ? "translate-y-0 opacity-100" : "translate-y-12 opacity-0"}`}>
            {problemCards.map((card, index) => {
            const Icon = card.icon;
            return <Card key={index} data-index={index} className="bg-[#112234] border-border/50 shadow-sm transition-shadow hover:shadow-md py-4 gap-3 border-t-2 border-[#00c4b4]">
                  <CardHeader className="pb-1">
                    <div className="text-[#00c4b4] mb-4 inline-flex size-12 items-center justify-center rounded-lg !bg-transparent">
                      <Icon className="size-6" />
                    </div>
                    <CardTitle className="text-xl text-white">{card.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-base leading-relaxed text-white/70">
                      {card.description}
                    </CardDescription>
                  </CardContent>
                </Card>;
          })}
          </div>

          <div ref={alertRef} className={`mt-4 w-full max-w-4xl transition-all duration-1000 delay-400 ${isAlertVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
            <Alert className="bg-[#112234] text-[#00c4b4] border-[#00c4b4]/20 items-center py-5">
              <CircleAlert className="size-5" />
              <AlertDescription className="col-start-2 grid justify-items-start gap-1 [&_p]:leading-relaxed text-base font-medium !text-[hsl(0,0%,100%)] md:text-lg">The result: projects run over budget, PMs are hard to hold accountable, and clients feel like they have no control over their own investment.</AlertDescription>
            </Alert>
          </div>
        </div>

        <div className="my-10 flex justify-center md:my-12">
          <Separator className="w-24 md:w-32 bg-[#00c4b4]" />
        </div>

        <div className="flex flex-col items-center">
          <div ref={costHeaderRef} className={`flex max-w-4xl flex-col items-center text-center transition-all duration-1000 ${isCostHeaderVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
            <span className="text-muted-foreground mb-4 text-xs font-bold tracking-widest uppercase">
              The Cost | Significant cost in Traditional Project Management
            </span>
            <h2 className="text-4xl font-extrabold tracking-tight text-white md:text-5xl lg:text-6xl">Staying With the Old Model Costs You More Than You Think.</h2>
          </div>

          <div ref={statsRef} className={`mt-6 grid w-full max-w-5xl grid-cols-1 gap-6 md:grid-cols-2 md:gap-8 transition-all duration-1000 delay-200 ${areStatsVisible ? "translate-y-0 opacity-100" : "translate-y-12 opacity-0"}`}>
            {costStats.map((stat, index) => <div key={index} data-index={index} className="flex flex-col items-center text-center md:items-start md:text-left">
                <span className="text-white mb-4 text-6xl font-black tracking-tighter md:text-7xl lg:text-8xl">
                  {stat.value}
                </span>
                <p className="text-white/70 text-lg font-medium leading-relaxed md:text-xl">
                  {stat.description}
                </p>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}